import { useFormikContext } from "formik";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { ArgumentTypes } from "../../utils/TypeScriptUtils";
import { BrowserHistoryContext } from "../../url/WSRouter";
import { unsavedChangesDialog } from "../../business-components/common/unsavedChanges";

export const useUnsavedChangesBlockerId = () => {
  return useMemo(() => `${++unsavedChangesBlockerId}`, []);
};

let unsavedChangesBlockerId = 0;

/**
 * Navigation blocking. (Shows dialog if form is dirty)
 * Use useBlockCallbackIfDirtyForms for state blocking.
 *
 * Wrap app in <UnsavedChangesHandler /> to enable this functionality.
 */

export interface UnsavedChangesBlockerProps {
  isDirty?: boolean;
  id?: string;
}
export const UnsavedChangesBlocker = (props: UnsavedChangesBlockerProps) => {
  let { isDirty, id: idFromProps } = props;

  if (isDirty === undefined) {
    const formik = useFormikContext();
    formik.handleReset;
    isDirty = formik.dirty;
  }

  const context = useContext(UnsavedChangesHandlerContext);

  const _defaultId = useUnsavedChangesBlockerId();
  const id = idFromProps ?? _defaultId;

  const isRegisteredDirty = useRef(false);

  useEffect(() => {
    if (isDirty && !isRegisteredDirty.current) {
      context.registerDirtyForm(id);
      isRegisteredDirty.current = true;
    }

    if (!isDirty && isRegisteredDirty.current) {
      context.unregisterDirtyForm(id);
      isRegisteredDirty.current = false;
    }

    return () => {
      context.unregisterDirtyForm(id);
    };
  }, [isDirty]);

  return null;
};

/**
 * State blocking. (Shows dialog if ANY form is dirty)
 * Use <FormikUnsavedChangesBlocker /> for navigation blocking.
 *
 * Wrap app in <UnsavedChangesHandler /> to enable this functionality.
 */
export function useBlockCallbackIfDirtyForms<F extends Function>(callback: F) {
  const { dirtyFormsRef } = useContext(UnsavedChangesHandlerContext);

  return async (...args: ArgumentTypes<F>) => {
    const anyDirty = Object.keys(dirtyFormsRef.current).length > 0;

    let ok = !anyDirty;
    if (!ok) {
      ok = await unsavedChangesDialog();
    }

    if (ok) {
      callback(...args);
      return;
    }
  };
}

export interface UnsavedChangesHandlerProps {
  children: React.ReactNode;
}

/**
 * Wrap app in this component to enable navigation and state blocking.
 */
export const UnsavedChangesHandler = (props: UnsavedChangesHandlerProps) => {
  const { children } = props;

  const dirtyFormsRef = useRef<{ [key: string]: boolean }>({});

  const _unblock = useRef<() => void | null>(null);

  const unblock = () => {
    _unblock.current?.();
    _unblock.current = null;
  };

  const registerDirtyForm = (formName: string) => {
    dirtyFormsRef.current = {
      ...dirtyFormsRef.current,
      [formName]: true,
    };

    if (!_unblock.current) {
      _unblock.current = browserHistory.block(async (blocker) => {
        const shouldNavigate = await unsavedChangesDialog();
        if (shouldNavigate) {
          unblock();
          blocker.retry();
        }
      });
    }
  };

  const unregisterDirtyForm = (formName: string) => {
    const newVal = {
      ...dirtyFormsRef.current,
    };

    delete newVal[formName];

    dirtyFormsRef.current = newVal;

    if (Object.keys(newVal).length === 0) {
      unblock();
    }
  };

  const browserHistory = useContext(BrowserHistoryContext);

  const isLiveReloading = useRef(false);
  useEffect(() => {
    window.addEventListener("message", (evt) => {
      if (evt?.data?.type === "webpackInvalid") {
        isLiveReloading.current = true;
        unblock();
      }
    });
  }, []);

  return (
    <UnsavedChangesHandlerContext.Provider
      value={{
        registerDirtyForm,
        unregisterDirtyForm,
        dirtyFormsRef,
      }}
    >
      {children}
    </UnsavedChangesHandlerContext.Provider>
  );
};

export interface UnsavedChangesHandlerContextValue {
  registerDirtyForm: (formName: string) => void;
  unregisterDirtyForm: (formName: string) => void;
  dirtyFormsRef: React.MutableRefObject<{
    [key: string]: boolean;
  }>;
}

export const UnsavedChangesHandlerContext =
  createContext<UnsavedChangesHandlerContextValue>(null as any);
